import React from 'react';
import { View, type ViewProps } from 'react-native';
import {
  Box,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

interface TimerViewProps extends ViewProps {
  className?: string;
}
const TimerView = View as React.ComponentType<TimerViewProps>;

interface ExamStatus {
  isOpen: boolean;
  openedAt: string | null;
  closedAt: string | null;
  autoCloseAt: string | null;
  timeRemainingSeconds?: number;
}

interface TimerDisplayProps {
  examStatus: ExamStatus;
  compact?: boolean;
}

const WARNING_THRESHOLD_SECONDS = 300; // 5 minutes

function formatCountdown(totalSeconds: number): string {
  if (totalSeconds <= 0) return '00:00';
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatClockTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getUTCHours();
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export const TimerDisplay: React.FC<TimerDisplayProps> = ({ examStatus, compact = false }) => {
  const { t } = useTranslation();

  // Don't render the countdown when there's no time limit
  if (examStatus.timeRemainingSeconds == null && !examStatus.autoCloseAt) {
    return null;
  }

  const timeRemaining = examStatus.timeRemainingSeconds ?? 0;
  const isWarning = timeRemaining <= WARNING_THRESHOLD_SECONDS;

  return (
    <Box mb="$2">
      <TimerView
        testID="mcq-countdown-timer"
        className={isWarning ? 'warning' : ''}
      >
        <Box
          bg={isWarning ? '$error50' : '$bgCard'}
          borderRadius="$lg"
          p={compact ? '$2' : '$3'}
          borderWidth={1}
          borderColor={isWarning ? '$error300' : '$borderLight'}
          alignItems="center"
        >
          <Text
            size={compact ? 'xl' : '2xl'}
            bold
            color={isWarning ? '$error600' : '$textHeading'}
          >
            {formatCountdown(timeRemaining)}
          </Text>
        </Box>
      </TimerView>

      {isWarning && (
        <Box testID="mcq-time-warning-message" mt="$2" alignItems="center">
          <Text size="sm" color="$error600" bold textAlign="center">
            {t('student.mcq.timeRunningOut')}
          </Text>
        </Box>
      )}

      {examStatus.openedAt && (
        <Box testID="mcq-exam-info" mt="$2">
          <HStack justifyContent="space-between">
            <Text size="xs" color="$textSubtle">
              {t('student.mcq.startedAt', { time: formatClockTime(examStatus.openedAt) })}
            </Text>
            {examStatus.autoCloseAt && (
              <Text size="xs" color="$textSubtle">
                {t('student.mcq.closesAt', { time: formatClockTime(examStatus.autoCloseAt) })}
              </Text>
            )}
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default TimerDisplay;
